import { useCallback, useEffect, useRef, useState } from 'react';

/** How long after the menu's exit transition the tint is let go. */
const RELEASE_AFTER_EXIT_MS = 100;

/**
 * When the page behind a full-screen mobile menu should wear the menu's
 * colour.
 *
 * A fixed panel stops at the top of Safari's floating toolbar; only the page
 * itself paints behind it. So while the menu is open the page takes its colour
 * — otherwise a pale band of page background shows through the toolbar under
 * the panel. The caller renders the `GlobalStyles` that does that while
 * `tintPage` is true.
 *
 * Safari only re-reads the colours it tints its toolbar from while no modal is
 * up, which makes the timing lopsided:
 *  - ON has to land in the same render that opens the menu, before the
 *    drawer's modal is in place. An effect is already too late — the toolbar
 *    stayed pale.
 *  - OFF has to wait until the drawer has finished closing, or the change is
 *    ignored and the toolbar stays tinted afterwards.
 * So the tint follows `open` directly, plus a hold that outlives it until the
 * drawer's exit transition reports in.
 * @param open - Whether the full-screen menu is open
 * @param hasExitTransition - False when the menu, as currently laid out, has
 *   no exit transition to wait for (the permanent drawer on a wide screen, or
 *   one that is `display: none`). `onExited` never runs there, so the hold is
 *   dropped at once rather than left to tint the page the next time the
 *   window narrows.
 * @returns `tintPage`, and the `onExited` to give the drawer's transition
 */
export function usePageTintWhileOpen(open: boolean, hasExitTransition = true) {
  const openRef = useRef(open);
  openRef.current = open;
  const [held, setHeld] = useState(open);
  const releaseTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (open) {
      setHeld(true);
      return;
    }
    if (!hasExitTransition) setHeld(false);
  }, [open, hasExitTransition]);

  useEffect(() => () => window.clearTimeout(releaseTimer.current), []);

  const onExited = useCallback(() => {
    window.clearTimeout(releaseTimer.current);
    releaseTimer.current = window.setTimeout(() => {
      // Reopened in the meantime: the tint is the new opening's to keep.
      if (!openRef.current) setHeld(false);
    }, RELEASE_AFTER_EXIT_MS);
  }, []);

  return { tintPage: open || (hasExitTransition && held), onExited };
}
